import { Check, CheckCircle, Star } from 'lucide-react';
import type { BaseComponentProps } from '../../types/puck';
import { LUCIDE_ICONS } from "../../lib/lucide-icons";
import { cn } from "../../lib/utils";

interface BenefitItem {
  text: string;
  icon?: string;
}

interface BenefitsListProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  benefits: BenefitItem[];
  layout: "grid" | "list" | "centered";
  iconStyle: "check" | "checkCircle" | "star" | "custom";
  columns: "1" | "2" | "3" | "4";
  backgroundColor?: string;
  accentColor?: string;
}

const BenefitsList: React.FC<BenefitsListProps> = ({
  heading,
  subheading,
  benefits,
  layout = "grid",
  iconStyle = "check",
  columns = "3",
  backgroundColor,
  accentColor,
  id,
  className,
}) => {
  // A one-column grid is still a grid: "Grid Cards" used to fall through to the stack
  // Renderer here, so picking it silently produced no cards at all.
  const isStackLayout = layout === "list",
    surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const,

    getIcon = (benefit: BenefitItem) => {
      if (iconStyle === "custom" && benefit.icon) {
        const IconComponent = LUCIDE_ICONS[benefit.icon];
        if (IconComponent) {
          return <IconComponent className="jobsite:w-4.5 jobsite:h-4.5" strokeWidth={2} />;
        }
      }

      switch (iconStyle) {
        case "checkCircle": {
          return <CheckCircle className="jobsite:w-4.5 jobsite:h-4.5" strokeWidth={2} />;
        }
        case "star": {
          return <Star className="jobsite:w-4.5 jobsite:h-4.5" strokeWidth={2} />;
        }
        default: {
          return <Check className="jobsite:w-4.5 jobsite:h-4.5" strokeWidth={2.5} />;
        }
      }
    },

    getGridCols = () => {
      switch (columns) {
        case "1": {
          return "jobsite:grid-cols-1";
        }
        case "2": {
          return "jobsite:grid-cols-1 jobsite:md:grid-cols-2";
        }
        case "4": {
          return "jobsite:grid-cols-1 jobsite:md:grid-cols-2 jobsite:lg:grid-cols-4";
        }
        default: {
          return "jobsite:grid-cols-1 jobsite:md:grid-cols-2 jobsite:lg:grid-cols-3";
        }
      }
    },

    getContentFrameClass = () => {
      if (layout === "centered") {
        return "jobsite:mx-auto jobsite:max-w-5xl";
      }
      if (isStackLayout) {
        return "jobsite:mx-auto jobsite:max-w-3xl";
      }

      switch (columns) {
        case "2": {
          return "jobsite:mx-auto jobsite:max-w-5xl";
        }
        case "4": {
          return "jobsite:mx-auto jobsite:max-w-7xl";
        }
        default: {
          return "jobsite:mx-auto jobsite:max-w-6xl";
        }
      }
    },

    accentBgStyle = accentColor
      ? { backgroundColor: `${accentColor  }14`, color: accentColor }
      : undefined,
    accentTextStyle = accentColor ? { color: accentColor } : undefined,

    renderIcon = (benefit: BenefitItem) => (
      <div
        className="jobsite:flex jobsite:items-center jobsite:justify-center jobsite:shrink-0 jobsite:w-9 jobsite:h-9 jobsite:rounded-full jobsite:bg-accent/15 jobsite:text-accent"
        style={accentBgStyle}
      >
        {getIcon(benefit)}
      </div>
    ),

    renderGridLayout = () => (
      <div className={cn("jobsite:grid jobsite:gap-4 jobsite:md:gap-5", getGridCols())}>
        {benefits.map((benefit, idx) => (
          <div
            key={idx}
            className="jobsite:group jobsite:flex jobsite:items-center jobsite:gap-4 jobsite:border jobsite:border-border/60 jobsite:bg-background jobsite:p-5 jobsite:transition-all jobsite:duration-200 jobsite:hover:-translate-y-0.5 jobsite:hover:border-primary/20 jobsite:hover:shadow-sm"
            style={surfaceRadiusStyle}
          >
            {renderIcon(benefit)}
            <p className="jobsite:min-w-0 jobsite:text-base jobsite:font-medium jobsite:leading-snug jobsite:text-foreground">
              {benefit.text}
            </p>
          </div>
        ))}
      </div>
    ),

    renderListLayout = () => (
      <div className="jobsite:mx-auto jobsite:max-w-2xl jobsite:space-y-4">
        {benefits.map((benefit, idx) => (
          <div key={idx} className="jobsite:flex jobsite:min-w-0 jobsite:items-center jobsite:gap-4">
            {renderIcon(benefit)}
            <p className="jobsite:min-w-0 jobsite:text-base jobsite:font-medium jobsite:leading-snug jobsite:text-foreground jobsite:sm:text-lg">
              {benefit.text}
            </p>
          </div>
        ))}
      </div>
    ),

    renderCenteredLayout = () => (
      <div className="jobsite:flex jobsite:flex-wrap jobsite:justify-center jobsite:gap-3">
        {benefits.map((benefit, idx) => (
          <div
            key={idx}
            className="jobsite:flex jobsite:items-center jobsite:gap-2.5 jobsite:border jobsite:border-border/50 jobsite:bg-background jobsite:px-5 jobsite:py-3 jobsite:shadow-sm"
            style={surfaceRadiusStyle}
          >
            <div className="jobsite:text-primary jobsite:shrink-0" style={accentTextStyle}>
              {getIcon(benefit)}
            </div>
            <span className="jobsite:text-sm jobsite:font-semibold jobsite:text-foreground jobsite:sm:text-base">
              {benefit.text}
            </span>
          </div>
        ))}
      </div>
    );

  return (
    <section
      id={id}
      className={cn(
        "jobsite:py-16 jobsite:md:py-24",
        backgroundColor ? "" : "jobsite:bg-background",
        className,
      )}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        <div className={getContentFrameClass()}>
          <div
            className={cn(
              "jobsite:mx-auto jobsite:mb-10 jobsite:max-w-3xl jobsite:space-y-3 jobsite:text-center jobsite:md:mb-14",
              isStackLayout && "jobsite:max-w-2xl",
            )}
          >
            <h2 className="jobsite:font-heading jobsite:text-3xl jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground jobsite:sm:text-4xl">
              {heading}
            </h2>
            {subheading && (
              <p className="jobsite:text-lg jobsite:leading-relaxed jobsite:text-muted-foreground">
                {subheading}
              </p>
            )}
          </div>

          {layout === "grid" && renderGridLayout()}
          {layout === "list" && renderListLayout()}
          {layout === "centered" && renderCenteredLayout()}
        </div>
      </div>
    </section>
  );
};

export type { BenefitItem, BenefitsListProps };
export { BenefitsList };
